import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { Center } from "@astryxdesign/core/Center";
import { CodeBlock } from "@astryxdesign/core/CodeBlock";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { Spinner } from "@astryxdesign/core/Spinner";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { useToast } from "@astryxdesign/core/Toast";
import { ToolShell } from "@karrtopelka/tools-kit/ToolShell";
import { useEffect, useState } from "react";

interface ConnInfo {
  ip: string;
  country: string | null;
  city: string | null;
  region: string | null;
  continent: string | null;
  timezone: string | null;
  latitude: string | null;
  longitude: string | null;
  postalCode: string | null;
  asn: number | null;
  asOrganization: string | null;
  colo: string | null;
  httpProtocol: string | null;
  tlsVersion: string | null;
  tlsCipher: string | null;
  userAgent: string | null;
}

/** Flag emoji from an ISO-3166 alpha-2 code, via regional indicator symbols. */
function flag(cc: string | null): string {
  if (!cc || cc.length !== 2) return "";
  const base = 0x1f1e6;
  const a = cc.toUpperCase().charCodeAt(0) - 65;
  const b = cc.toUpperCase().charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return "";
  return String.fromCodePoint(base + a, base + b);
}

const dash = "—";

export function App() {
  const [data, setData] = useState<ConnInfo | null>(null);
  const [failed, setFailed] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let live = true;
    fetch("/json")
      .then((r) => r.json() as Promise<ConnInfo>)
      .then((d) => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const copy = (value: string, what: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast({ body: `${what} copied`, uniqueID: "copy" }))
      .catch(() => toast({ body: "Copy failed", type: "error", uniqueID: "copy" }));
  };

  const location = data
    ? [data.city, data.region, data.country].filter(Boolean).join(", ") || null
    : null;

  return (
    <ToolShell active="ip" tagline="What the internet sees when you connect">
      {failed ? (
        <Center minHeight={240}>
          <EmptyState
            title="Could not read your connection"
            description="Reload the page — this tool needs a live request to report on."
          />
        </Center>
      ) : !data ? (
        <Center minHeight={240}>
          <Spinner size="lg" label="Reading your connection" />
        </Center>
      ) : (
        <>
          {/* The one value most visitors came for — big, selectable, one tap to copy. */}
          <Card padding={4}>
            <VStack gap={2}>
              <Text type="supporting" size="xsm" color="secondary">
                Your IP address
              </Text>
              <HStack gap={2} align="center" justify="between" wrap="wrap">
                <Text type="body" size="2xl" weight="bold" color="accent" hasTabularNumbers>
                  {data.ip}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  label="Copy IP"
                  onClick={() => copy(data.ip, "IP")}
                />
              </HStack>
              {location ? (
                <Text type="supporting" size="xsm" color="secondary">
                  {flag(data.country)} {location}
                </Text>
              ) : null}
            </VStack>
          </Card>

          <VStack gap={1}>
            <Text type="label" color="secondary">
              Network
            </Text>
            <Card padding={3}>
              <MetadataList columns="multi">
                <MetadataListItem label="ASN">
                  {data.asn ? `AS${data.asn}` : dash}
                </MetadataListItem>
                <MetadataListItem label="Network">
                  {data.asOrganization ?? dash}
                </MetadataListItem>
                <MetadataListItem label="Edge">{data.colo ?? dash}</MetadataListItem>
                <MetadataListItem label="Protocol">{data.httpProtocol ?? dash}</MetadataListItem>
                <MetadataListItem label="TLS">{data.tlsVersion ?? dash}</MetadataListItem>
                <MetadataListItem label="Cipher">{data.tlsCipher ?? dash}</MetadataListItem>
              </MetadataList>
            </Card>
          </VStack>

          <VStack gap={1}>
            <Text type="label" color="secondary">
              Location
            </Text>
            <Card padding={3}>
              <MetadataList columns="multi">
                <MetadataListItem label="Country">
                  {data.country ? `${flag(data.country)} ${data.country}` : dash}
                </MetadataListItem>
                <MetadataListItem label="City">{data.city ?? dash}</MetadataListItem>
                <MetadataListItem label="Region">{data.region ?? dash}</MetadataListItem>
                <MetadataListItem label="Postal code">{data.postalCode ?? dash}</MetadataListItem>
                <MetadataListItem label="Time zone">{data.timezone ?? dash}</MetadataListItem>
                <MetadataListItem label="Coordinates">
                  {data.latitude && data.longitude ? `${data.latitude}, ${data.longitude}` : dash}
                </MetadataListItem>
              </MetadataList>
              <VStack paddingBlock={1}>
                <Text type="supporting" size="xsm" color="secondary">
                  Location is inferred from your IP by Cloudflare's edge. It is approximate,
                  and a VPN will move it.
                </Text>
              </VStack>
            </Card>
          </VStack>

          <VStack gap={1}>
            <Text type="label" color="secondary">
              Browser
            </Text>
            <Card padding={3}>
              <MetadataList>
                <MetadataListItem label="User agent">{data.userAgent ?? dash}</MetadataListItem>
              </MetadataList>
            </Card>
          </VStack>

          <VStack gap={1}>
            <Text type="label" color="secondary">
              From the terminal
            </Text>
            <CodeBlock
              language="bash"
              width="100%"
              isWrapped
              code={`curl ip.karrtopelka.com        # just the IP\ncurl ip.karrtopelka.com/json   # everything above`}
            />
          </VStack>

          <Text type="supporting" size="xsm" color="secondary">
            Nothing on this page is stored or logged. Every value is read from your request
            and thrown away when the response is sent.
          </Text>
        </>
      )}
    </ToolShell>
  );
}
